import { MainPath, TileNames } from "Constants/constants";

import { Step } from "@storebrand-digital/insurance-stepper";
import { Cart } from "Store/cart/cartInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { getProductSubtitle } from "Store/product/productUtils";
import { formatProductName } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { getProductName } from "Utils/nameUtils/nameUtils";

export const getCheckoutSteps = (
  cart: Cart | null,
  checkoutOrderStepTitle: string,
): Step[] => {
  if (!isDefined(cart)) {
    return [];
  }

  return [
    ...getCheckoutProductsSteps(cart.products),
    getCheckoutOrderStep(checkoutOrderStepTitle),
  ];
};

export const getFirstCheckoutStepId = (cart: Cart): string =>
  getCheckoutProductsSteps(cart.products)[0]?.id ?? TileNames.CHECKOUT;

export const getLastCheckoutProductStepId = (cart: Cart): string | undefined =>
  getCheckoutProductsSteps(cart.products).slice(-1)[0]?.id;

const getCheckoutProductsSteps = (products: InsuranceProduct[]): Step[] =>
  products.filter(shouldDisplayProductCheckoutStep).map((product) => {
    const stepTitle = getProductName(product);
    return {
      id: product.productId,
      title: stepTitle,
      ariaLabel: `${MainPath.CHECKOUT}-${stepTitle.toLowerCase()}`,
      complete: false,
      type: formatProductName(product.name),
      subTitle: getProductSubtitle(product),
    };
  });

const shouldDisplayProductCheckoutStep = (product: InsuranceProduct): boolean =>
  !productHasNoDetails(product);

const getCheckoutOrderStep = (title: string): Step => {
  const stepId = TileNames.CHECKOUT;
  return {
    id: stepId,
    title: title,
    ariaLabel: `${MainPath.CHECKOUT}-${stepId}`,
    complete: false,
  };
};

const productHasNoDetails = (product: InsuranceProduct): boolean => {
  if (productAlwaysHasDetails(product)) {
    return false;
  }
  return (
    product.isReadyForPurchase ||
    product.name === INSURANCE_NAME_VALUE.DEATH ||
    product.name === INSURANCE_NAME_VALUE.CRITICAL_ILLNESS ||
    product.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX ||
    product.name === INSURANCE_NAME_VALUE.HEALTH ||
    product.name === INSURANCE_NAME_VALUE.CHILD_HEALTH ||
    ((product.name === INSURANCE_NAME_VALUE.AKA_HEALTH ||
      product.name === INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS ||
      product.name === INSURANCE_NAME_VALUE.AKA_GROUP_LIFE) &&
      !product.coInsuredApplicantPersonInformation)
  );
};

/**
 * Health products without signing (no separate EDH process) must always
 * have details (also when accepting offer from advisor) to make sure that
 * the customer is shown the confirmNoSeriousIllness checkbox.
 */
export const productAlwaysHasDetails = (product: InsuranceProduct): boolean =>
  product.name === INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING ||
  product.name === INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING;
